import dayjs, { Dayjs } from 'dayjs';

import { PickupOrderResponse, PickupOrderShipment } from '@/api/data-contracts';

export interface PendingPickupGroup {
  key: string;
  order: PickupOrderResponse;
  senderName: string;
  ordered: boolean;
  awbCount: number;
  parcels: number;
  envelopes: number;
  weight: number;
}

export interface PendingPickupRow {
  rowId: string;
  groupKey: string;
  shipment: PickupOrderShipment;
}

const NOT_COLLECTED = 'REGISTERED';

export const pendingPickupGroupKey = (order: PickupOrderResponse): string =>
  order.id ?? `${order.clientId}|${order.pickupDate}`;

const summarize = (
  order: PickupOrderResponse,
  shipments: PickupOrderShipment[],
  senderNameByClientId: Record<string, string>,
): PendingPickupGroup => ({
  key: pendingPickupGroupKey(order),
  order,
  senderName: senderNameByClientId[order.clientId] ?? order.clientId,
  ordered: !!order.id,
  awbCount: shipments.length,
  parcels: shipments.reduce((sum, shipment) => sum + (shipment.parcels ?? 0), 0),
  envelopes: shipments.reduce((sum, shipment) => sum + (shipment.envelopes ?? 0), 0),
  weight: shipments.reduce((sum, shipment) => sum + (shipment.weight ?? 0), 0),
});

export const buildPendingPickups = (
  waiting: PickupOrderResponse[],
  ordered: PickupOrderResponse[],
  senderNameByClientId: Record<string, string>,
): { groups: PendingPickupGroup[]; rows: PendingPickupRow[] } => {
  const entries = [
    ...waiting.map((order) => ({ order, shipments: order.shipments })),
    ...ordered
      .filter((order) => order.status === 'PLACED')
      .map((order) => ({
        order,
        shipments: order.shipments.filter((shipment) => shipment.status === NOT_COLLECTED),
      })),
  ]
    .filter((entry) => entry.shipments.length > 0)
    .sort((a, b) => a.order.pickupDate.localeCompare(b.order.pickupDate));

  const groups = entries.map((entry) =>
    summarize(entry.order, entry.shipments, senderNameByClientId),
  );
  const rows = entries.flatMap((entry) =>
    entry.shipments.map(
      (shipment): PendingPickupRow => ({
        rowId: shipment.id,
        groupKey: pendingPickupGroupKey(entry.order),
        shipment,
      }),
    ),
  );

  return { groups, rows };
};

export const filterPendingPickupRows = (
  rows: PendingPickupRow[],
  clientIds: string[],
  groups: PendingPickupGroup[],
): PendingPickupRow[] => {
  if (clientIds.length === 0) return rows;
  const allowed = new Set(
    groups.filter((group) => clientIds.includes(group.order.clientId)).map((group) => group.key),
  );
  return rows.filter((row) => allowed.has(row.groupKey));
};

export const searchPendingPickupRows = (
  rows: PendingPickupRow[],
  search: string,
  groups: PendingPickupGroup[],
): PendingPickupRow[] => {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  const senderByKey = new Map(groups.map((group) => [group.key, group.senderName]));
  return rows.filter(({ shipment, groupKey }) =>
    [
      shipment.awbNumber,
      shipment.orderNumber,
      shipment.recipientName,
      shipment.recipientLocality,
      senderByKey.get(groupKey),
    ].some((value) => value?.toLowerCase().includes(term)),
  );
};

export const resolveSelectedGroup = (
  selected: PendingPickupRow[],
  groups: PendingPickupGroup[],
): PendingPickupGroup | undefined => {
  const keys = new Set(selected.map((row) => row.groupKey));
  if (keys.size !== 1) return undefined;
  return groups.find((item) => keys.has(item.key));
};

export const resolveSelectedWaitingGroup = (
  selected: PendingPickupRow[],
  groups: PendingPickupGroup[],
): PendingPickupGroup | undefined => {
  const group = resolveSelectedGroup(selected, groups);
  return group && !group.ordered ? group : undefined;
};

export const isPastPickupDate = (pickupDate: string, now: Dayjs = dayjs()): boolean =>
  dayjs(pickupDate).isBefore(now, 'day');
